import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowRight, BookOpen } from 'lucide-react';
import './Blog.css';

// ── Centralized Blog Post Data ──
export const blogPosts = [
  {
    slug: 'cloud-migration-checklist-fintech',
    title: 'Cloud Migration Checklist for Indian Fintech Companies (2025)',
    excerpt: 'Migrating financial platforms to the cloud requires navigating strict RBI guidelines, data localization laws, and airtight security. Here is our compliance-first architectural blueprint.',
    category: 'Cloud & DevOps',
    author: 'Amit Sharma',
    role: 'Principal Cloud Architect',
    date: 'May 15, 2026',
    readTime: '6 min read',
    tags: ['Cloud Migration', 'FinTech', 'RBI Compliance', 'AWS'],
    accentColor: '#06b6d4',
    coverGradient: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'The Indian fintech sector is undergoing unprecedented expansion, powered by digital payments, micro-lending, and innovative wealthtech platforms. However, migrating sensitive financial workloads to public or hybrid cloud environments requires much more than just simple technical re-hosting. Indian fintechs operate under the watchful eye of the Reserve Bank of India (RBI), making compliance, data localization, and strict risk management the absolute foundation of any cloud strategy.'
      },
      {
        type: 'heading',
        text: '1. Regulatory Compliance & Data Localization (RBI Mandate)'
      },
      {
        type: 'paragraph',
        text: 'The RBI is extremely clear about data sovereignty. All transaction details, customer personal identifiable information (PII), and financial history must be stored strictly within the geographic boundaries of India. When setting up cloud infrastructure (e.g., AWS, Microsoft Azure, or GCP), architectures must be deployed exclusively in Indian cloud regions (such as AWS Asia Pacific Mumbai `ap-south-1` or Hyderabad `ap-south-2`).'
      },
      {
        type: 'list',
        items: [
          'Ensure complete end-to-end data storage within India limits, including backups, logs, and audit trails.',
          'Implement strictly isolated databases with envelope encryption (using Indian region KMS keys).',
          'Deploy continuous logging and real-time monitoring accessible by national regulatory auditors.'
        ]
      },
      {
        type: 'heading',
        text: '2. Airtight Security & Access Control'
      },
      {
        type: 'paragraph',
        text: 'Zero trust is not a buzzword; in fintech, it is a survival requirement. Implementing Least Privilege Access across your entire Kubernetes and cloud infrastructure guarantees that compromised microservices cannot access master databases.'
      },
      {
        type: 'code',
        code: `# Example IAM policy restricting database access in AWS
{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": ["rds-db:connect"],
      "Resource": ["arn:aws:rds-db:ap-south-1:123456789012:dbuser:db-XYZ/*"]
    }
  ]
}`
      },
      {
        type: 'quote',
        text: 'Security in fintech is a layered puzzle. One single misconfigured security group can lead to devastating data leaks and regulatory penalties.'
      },
      {
        type: 'paragraph',
        text: 'By checking each step of our migration blueprint—focusing on regulatory compliance, end-to-end encryption, and automated CI/CD pipelines—Indian fintech platforms can transition to the cloud safely and scale to millions of active transactions daily.'
      }
    ]
  },
  {
    slug: 'zero-trust-sme-implementation',
    title: 'Zero Trust Security: How Indian SMEs Can Implement It in 90 Days',
    excerpt: 'Cyberattacks on mid-sized enterprises have spiked by 150%. This actionable 90-day guide details how to implement zero trust without enterprise-level budgets.',
    category: 'Cybersecurity',
    author: 'Siddharth Verma',
    role: 'Cybersecurity Lead',
    date: 'May 10, 2026',
    readTime: '7 min read',
    tags: ['Zero Trust', 'SME Security', 'IAM', 'MFA'],
    accentColor: '#ef4444',
    coverGradient: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'Many small and medium-sized enterprises (SMEs) in India believe that Zero Trust security is an expensive luxury reserved exclusively for Fortune 500 giants. However, as supply chain attacks increase and ransomware groups aggressively target mid-market firms, Zero Trust has transitioned into an operational necessity. This roadmap outlines how to transition your SME network architecture to Zero Trust within 90 days, relying heavily on existing systems and open standards.'
      },
      {
        type: 'heading',
        text: 'Days 1-30: Comprehensive Identity & Access Verification'
      },
      {
        type: 'paragraph',
        text: 'The core tenet of Zero Trust is "Never Trust, Always Verify." In the first month, your primary objective is to gain absolute control over who has access to your resources, starting with mandatory Multi-Factor Authentication (MFA).'
      },
      {
        type: 'list',
        items: [
          'Enforce hardware-key or authenticator app-based MFA across all corporate email accounts (Google Workspace, Microsoft 365).',
          'Conduct a thorough audit of all third-party contractor accesses and immediately revoke stagnant accounts.',
          'Consolidate user directories into a single Single Sign-On (SSO) portal to enforce strict session control policies.'
        ]
      },
      {
        type: 'heading',
        text: 'Days 31-60: Network Segmentation & Micro-Perimeters'
      },
      {
        type: 'paragraph',
        text: 'Once identities are locked down, you must prevent lateral movement. If an attacker breaches a customer support agent\'s laptop, they should never be able to reach your production code repos or financial accounting systems.'
      },
      {
        type: 'code',
        code: `# Restricting subnets within an internal network
# Only permit secure SSH/HTTPS to DB server from jump host subnet
iptables -A INPUT -p tcp -s 10.0.5.0/24 --dport 22 -j ACCEPT
iptables -A INPUT -p tcp --dport 22 -j DROP`
      },
      {
        type: 'quote',
        text: 'Traditional security focused on building strong castle walls. Zero Trust assumes the enemy has already scaled the walls, and places security guards at every single door inside the castle.'
      },
      {
        type: 'paragraph',
        text: 'By executing this phased 90-day roadmap, SMEs can dramatically decrease their vulnerability surface area, comply with emerging Indian cybersecurity frameworks, and protect crucial proprietary assets.'
      }
    ]
  },
  {
    slug: 'ai-automation-roi-metrics',
    title: 'AI Automation ROI: How to Measure Practical Results for Your Business',
    excerpt: 'AI sounds great on paper, but how does it translate to your bottom line? Learn how to calculate hard cost-savings, efficiency metrics, and real business value.',
    category: 'AI & Automation',
    author: 'Dr. Ramesh Nair',
    role: 'Director of AI & ML',
    date: 'May 05, 2026',
    readTime: '5 min read',
    tags: ['Artificial Intelligence', 'Business Automation', 'ROI Metrics', 'SaaS'],
    accentColor: '#7c3aed',
    coverGradient: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'Enterprise AI investments are at an all-time high, but boards of directors are starting to ask tough questions about returns on investment. Deploying LLM wrappers or generative search inside internal hubs is relatively easy, but mapping that deployment directly to financial growth and workload hours optimized is where most businesses fail. Let\'s cut through the hype and build a realistic mathematical framework for AI Automation ROI.'
      },
      {
        type: 'heading',
        text: 'The Three Pillars of Practical AI Metrics'
      },
      {
        type: 'paragraph',
        text: 'To successfully calculate ROI, you must categorize your automation savings into three clear buckets: direct labor cost reduction, system latency improvements, and downstream error mitigation.'
      },
      {
        type: 'list',
        items: [
          'Direct Hour Reclamation: Subtract the manual time spent on repetitive data matching before AI implementation from the time taken under automated workflows.',
          'Operational Throughput: Track how many additional customer queries, invoices, or tickets your team processes daily with the exact same headcount.',
          'Compliance and Error Reduction: Quantify the savings gained by eliminating human keystroke errors in sensitive financial or medical records.'
        ]
      },
      {
        type: 'quote',
        text: 'The ROI of AI is not in replacing your workforce; it is in turning a team of ten mid-level operators into an agile, highly productive powerhouse that outputs the equivalent of fifty people.'
      },
      {
        type: 'heading',
        text: 'An Easy ROI Equation'
      },
      {
        type: 'paragraph',
        text: 'You can express your net annual AI return using a simple ROI equation:'
      },
      {
        type: 'code',
        code: `AI ROI = [(Hours Saved per Month * Average Hourly Rate) - Monthly API/Hosting Overhead] * 12
Example:
- Hours saved: 320 hours/month
- Average rate: ₹1,500/hour
- Software/API cost: ₹50,000/month
- Net ROI = [(320 * 1500) - 50000] * 12 = (480000 - 50000) * 12 = ₹51.6 Lakhs/year`
      },
      {
        type: 'paragraph',
        text: 'Understanding and presenting these hard numbers to your executive team will validate current automation setups and build a bulletproof roadmap for scaling intelligent business features.'
      }
    ]
  },
  {
    slug: 'kubernetes-migration-mistakes',
    title: 'Top 5 Kubernetes Migration Mistakes and How to Avoid Them',
    excerpt: 'Migrating legacy monoliths to Kubernetes often leads to cloud spend inflation, slow deployments, and networking chaos. Here is how to avoid the common pitfalls.',
    category: 'Cloud & DevOps',
    author: 'Amit Sharma',
    role: 'Principal Cloud Architect',
    date: 'April 28, 2026',
    readTime: '8 min read',
    tags: ['Kubernetes', 'DevOps', 'Microservices', 'Docker'],
    accentColor: '#06b6d4',
    coverGradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'Kubernetes (K8s) is the undisputed king of container orchestration. However, in the rush to modernize IT architectures, many organizations treat Kubernetes like a silver bullet. The result? Teams end up lifting and shifting heavy monoliths directly into containers, only to be hit with massive cloud bills, unstable clusters, and extremely complicated deploy sequences. Here are the top mistakes we see during K8s migrations.'
      },
      {
        type: 'heading',
        text: 'Mistake 1: Ignoring Resource Requests and Limits'
      },
      {
        type: 'paragraph',
        text: 'By default, if you don\'t define CPU and memory requests/limits in your deployment manifests, Kubernetes scheduler will make arbitrary guesses. A single memory-leaking container can easily swallow the host node\'s entire RAM pool, causing other critical services to crash sequentially.'
      },
      {
        type: 'code',
        code: `# Always define proper resource requests and limits in your spec
resources:
  requests:
    memory: "256Mi"
    cpu: "250m"
  limits:
    memory: "512Mi"
    cpu: "500m"`
      },
      {
        type: 'heading',
        text: 'Mistake 2: Storing Stateful Data Inside Pods'
      },
      {
        type: 'paragraph',
        text: 'Containers are designed to be entirely ephemeral. If a pod crashes or is rescheduled, any local file storage is instantly wiped. Yet, developers often write uploads directly to localized container directories instead of setting up AWS S3 attachments or Kubernetes PersistentVolumes.'
      },
      {
        type: 'list',
        items: [
          'Store all structured user uploads and media assets on secure external Object Storage (AWS S3, Azure Blob).',
          'Use stateful database services (RDS, managed database hubs) instead of spinning up raw SQL inside unstable container nodes.',
          'Implement strictly stateless architectures so pods can spin down and scale up on-demand.'
        ]
      },
      {
        type: 'quote',
        text: 'Kubernetes is a highly sophisticated racing car. If you try to drive it like a legacy station wagon, you will inevitably crash and spend a fortune on maintenance.'
      },
      {
        type: 'paragraph',
        text: 'Avoiding these critical configuration mistakes ensures your Kubernetes environment is resilient, highly performant, and cost-efficient to run long-term.'
      }
    ]
  },
  {
    slug: 'hipaa-compliance-healthcare-it',
    title: 'HIPAA Compliance Roadmap for Indian Healthcare IT Teams',
    excerpt: 'Building digital health platforms for international markets? Here is the step-by-step security and compliance blueprint to pass HIPAA audits.',
    category: 'Cybersecurity',
    author: 'Siddharth Verma',
    role: 'Cybersecurity Lead',
    date: 'April 22, 2026',
    readTime: '7 min read',
    tags: ['HIPAA Compliance', 'Healthcare IT', 'Data Encryption', 'Security Audit'],
    accentColor: '#ef4444',
    coverGradient: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'Indian healthcare software companies are increasingly building remote wellness, telemedicine, and diagnostic software solutions for medical providers based in the United States and Europe. Doing so opens up incredible market opportunities, but it also carries massive regulatory obligations—most notably the Health Insurance Portability and Accountability Act (HIPAA). Failing to secure protected health information (PHI) can result in legal liabilities and immediate market blacklisting.'
      },
      {
        type: 'heading',
        text: '1. What Qualifies as PHI under HIPAA?'
      },
      {
        type: 'paragraph',
        text: 'PHI includes any patient record containing names, diagnostic histories, billing information, medical scans, or contact details. If a piece of data can be traced back to a specific individual and is associated with their health status, it must be encrypted and protected at all costs.'
      },
      {
        type: 'list',
        items: [
          'Enforce AES-256 encryption on all relational and non-relational database volumes storing patient history.',
          'Encrypt all PHI data in transit using TLS 1.3 protocols across public and internal microservices networks.',
          'De-identify diagnostic databases when piping records to development or machine learning environments.'
        ]
      },
      {
        type: 'heading',
        text: '2. Setting Up Secure Access and Immutable Audit Logs'
      },
      {
        type: 'paragraph',
        text: 'Under HIPAA guidelines, you must be able to prove exactly who accessed a patient\'s record, when they viewed it, and what modifications were made. These access logs must be entirely tamper-proof.'
      },
      {
        type: 'code',
        code: `# Secure cloud trail log configuration setup example
{
  "logGroup": "arn:aws:logs:ap-south-1:123456789012:log-group:hipaa-phi-audit",
  "encryptionKey": "arn:aws:kms:ap-south-1:123456789012:key/hipaa-kms-key",
  "enableLogFileValidation": true
}`
      },
      {
        type: 'quote',
        text: 'Compliance is not a checkbox event; it is an active engineering methodology that builds trust, protects patient vulnerability, and unlocks massive global pipelines.'
      },
      {
        type: 'paragraph',
        text: 'By implementing database encryption, continuous logging, and restricting employee environments, Indian IT teams can confidently deploy HIPAA-compliant software architectures.'
      }
    ]
  },
  {
    slug: 'how-we-cut-infra-costs-70-percent',
    title: 'Case Study: How We Cut Infrastructure Costs by 70% for a FinTech Client',
    excerpt: 'Our client faced ballooning AWS bills due to unoptimized database architectures and over-provisioned clusters. Here is how we re-engineered their cloud infrastructure.',
    category: 'Case Studies',
    author: 'Amit Sharma',
    role: 'Principal Cloud Architect',
    date: 'April 15, 2026',
    readTime: '6 min read',
    tags: ['Case Study', 'AWS Cost Optimization', 'Kubernetes', 'Database Optimization'],
    accentColor: '#10b981',
    coverGradient: 'linear-gradient(135deg, #064e3b 0%, #10b981 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'Managing cloud infrastructure costs is one of the most significant challenges for growing startups. Our client, a rapid-growth Indian retail wealth platform, experienced a massive spike in monthly active users, which caused their AWS hosting costs to skyrocket to over ₹12 Lakhs ($15,000) per month. They engaged Wayug Infotech to run a comprehensive cloud audit and restructure their setup for long-term scalability.'
      },
      {
        type: 'heading',
        text: 'The Discovery: What Was Swallowing Their Budget?'
      },
      {
        type: 'paragraph',
        text: 'During our deep-dive infrastructure audit, we discovered three primary architectural bottlenecks causing excessive cloud billing:'
      },
      {
        type: 'list',
        items: [
          'Over-provisioned EC2 instances running constantly at less than 12% average CPU utilization.',
          'Massive relational database instances set to multi-AZ deployment in dev/staging environments where simple single-node storage was sufficient.',
          'Absence of automated lifecycle rules on S3 buckets, leading to terabytes of raw debug logs being stored indefinitely on high-cost storage tiers.'
        ]
      },
      {
        type: 'heading',
        text: 'The Solution: Auto-Scaling, Spot Instances & S3 Tiering'
      },
      {
        type: 'paragraph',
        text: 'We implemented a phased, zero-downtime re-engineering plan. We containerized their stateless web nodes, migrated them to Amazon EKS (Kubernetes), and designed a hybrid cluster strategy combining AWS On-Demand and Spot instances.'
      },
      {
        type: 'code',
        code: `# Amazon EKS Node Group configuration snippet for Spot Instances
nodeGroups:
  - name: spot-workers
    instanceType: t3.medium
    spot: true
    minSize: 2
    maxSize: 10
    desiredCapacity: 2`
      },
      {
        type: 'quote',
        text: 'We were amazed by the immediate impact. Wayug didn\'t just give us a report; they rewrote our infrastructure scripts, yielding a 70% drop in billing within the first billing cycle.'
      },
      {
        type: 'paragraph',
        text: 'By migrating staging environments to Spot instances and optimizing database instances, we reduced the client\'s monthly AWS bill from ₹12 Lakhs to ₹3.6 Lakhs, saving them ₹1 Crore+ annually while actually improving system stability and loading speed.'
      }
    ]
  },
  {
    slug: 'rpa-vs-ai-automation-2026',
    title: 'RPA vs. AI Automation: Which Does Your Business Need in 2025?',
    excerpt: 'Confused between Robotic Process Automation (RPA) and AI-driven automation? Understand the technical differences and pick the right technology for your operations.',
    category: 'AI & Automation',
    author: 'Dr. Ramesh Nair',
    role: 'Director of AI & ML',
    date: 'April 08, 2026',
    readTime: '5 min read',
    tags: ['RPA', 'AI Automation', 'Workflow Engineering', 'Enterprise Software'],
    accentColor: '#7c3aed',
    coverGradient: 'linear-gradient(135deg, #4c1d95 0%, #8b5cf6 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'The automation landscape has shifted dramatically over the past two years. With the mainstreaming of generative AI and agentic systems, many business leaders are questioning if traditional tools like Robotic Process Automation (RPA) are obsolete. In reality, RPA and Artificial Intelligence serve completely different business needs. Choosing the wrong system will lead to costly implementation failures and missed expectations.'
      },
      {
        type: 'heading',
        text: 'RPA: Rule-Based Keystroke Mimicking'
      },
      {
        type: 'paragraph',
        text: 'RPA is excellent for handling static, highly predictable digital tasks. Think of it as a software robot that copies and pastes data across legacy ERP systems, exports weekly CSV logs, or logs into portal APIs using specific rules.'
      },
      {
        type: 'list',
        items: [
          'Inputs: Structured data (spreadsheets, standard forms, consistent databases).',
          'Logic: If-Else rule-based decision trees with zero room for ambiguity.',
          'Best For: Migrating massive ledgers, entering payroll details, and data-entry replication.'
        ]
      },
      {
        type: 'heading',
        text: 'AI Automation: Cognitive Decision-Making & NLP'
      },
      {
        type: 'paragraph',
        text: 'AI automation comes into play when data is unstructured and context is fluid. If your system needs to read customer feedback emails, understand sentiment, categorize attachments, and formulate appropriate replies, RPA cannot help. You need Natural Language Processing (NLP) and LLM-driven agents.'
      },
      {
        type: 'quote',
        text: 'RPA mimics human hands, executing tasks rapidly with zero thought. AI mimics human brains, analyzing information, evaluating risk, and determining the next best operational step.'
      },
      {
        type: 'paragraph',
        text: 'Modern enterprises shouldn\'t pick one over the other. The most efficient systems combine both—using RPA to scrape and package database files, and passing that data to AI to extract insights and generate business actions.'
      }
    ]
  },
  {
    slug: 'iso-27001-certification-guide',
    title: 'The Complete Guide to ISO 27001 Certification for Indian Companies',
    excerpt: 'Want to close lucrative global enterprise deals? Discover the step-by-step roadmap to implement ISMS and secure the gold standard of cybersecurity certification.',
    category: 'Cybersecurity',
    author: 'Siddharth Verma',
    role: 'Cybersecurity Lead',
    date: 'April 02, 2026',
    readTime: '6 min read',
    tags: ['ISO 27001', 'Data Security', 'Information Security', 'Compliance Guide'],
    accentColor: '#ef4444',
    coverGradient: 'linear-gradient(135deg, #7c1d12 0%, #ea580c 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'For Indian SaaS startups and IT firms looking to sign contracts with enterprise customers in North America, Europe, or Australia, information security is the ultimate entry ticket. Global CTOs will not trust vendor systems with their database assets unless they prove compliant with ISO 27001, the international gold standard for Information Security Management Systems (ISMS).'
      },
      {
        type: 'heading',
        text: 'Phase 1: Defining Scope & Information Security Policy'
      },
      {
        type: 'paragraph',
        text: 'Before seeking an audit, you must outline what systems are covered by your ISMS. Is it your entire company\'s physical corporate office network, or strictly your cloud SaaS hosting environment? Defining a tight, cloud-native scope will save you hundreds of audit hours.'
      },
      {
        type: 'list',
        items: [
          'Inventory all physical and digital assets containing company, customer, or operational data.',
          'Publish formal Security Policies signed off by the executive leadership and distribute them to employees.',
          'Conduct a comprehensive vulnerability analysis to evaluate security gaps in physical and remote environments.'
        ]
      },
      {
        type: 'heading',
        text: 'Phase 2: Implementing the Annex A Security Controls'
      },
      {
        type: 'paragraph',
        text: 'ISO 27001:2022 includes 93 security controls split across organizational, people, physical, and technological categories. You must create policies addressing access control, network security, cryptographic keys, and incident management.'
      },
      {
        type: 'code',
        code: `# Sample server config enforcing secure cryptographic key handling
# Disable weak SSH ciphers and enforce modern secure algorithms
KexAlgorithms curve25519-sha256,curve25519-sha256@libssh.org
Ciphers chacha20-poly1305@openssh.com,aes256-gcm@openssh.com`
      },
      {
        type: 'quote',
        text: 'ISO 27001 is not just an IT certificate; it is a profound business growth tool that builds trust, mitigates breaches, and opens doors to multi-million dollar contracts.'
      },
      {
        type: 'paragraph',
        text: 'By aligning your internal processes with ISO 27001 guidelines, you establish an bulletproof security culture that protects your company assets and speeds up client vendor assessments.'
      }
    ]
  },
  {
    slug: 'microservices-vs-monolith-migration',
    title: 'Microservices vs. Monolith: When to Migrate and When to Wait',
    excerpt: 'Is migrating to microservices actually worth the operational complexity? Learn the technical trade-offs and read our architectural decision guide.',
    category: 'Custom Software',
    author: 'Amit Sharma',
    role: 'Principal Cloud Architect',
    date: 'March 26, 2026',
    readTime: '8 min read',
    tags: ['Microservices', 'Monolith', 'System Architecture', 'API Gateway'],
    accentColor: '#2563eb',
    coverGradient: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'Every technology startup reaches a point where their initial application codebase—usually a monolithic framework—starts feeling slow, difficult to modify, and challenging to coordinate among expanding developer teams. The default architectural recommendation is to split everything into decoupled microservices. While microservices offer great scaling advantages, they also introduce complex network latency, distributed database challenges, and high operations overhead.'
      },
      {
        type: 'heading',
        text: 'When Monoliths Shine: Simplicity & Velocity'
      },
      {
        type: 'paragraph',
        text: 'If your business is still validating product-market fit or consists of a small engineering team (under 15 developers), a monolithic database is almost always superior. It is significantly faster to develop, deploys as a single unit, and avoids complex inter-service communication systems.'
      },
      {
        type: 'list',
        items: [
          'Unified code repository: Easier to run local testing and full integration sweeps.',
          'Simplified database transactions: Easy to run complex SQL joins without worrying about distributed networks.',
          'Lower operations overhead: No need to maintain highly complex Kubernetes clusters, tracing tools, or API gateways.'
        ]
      },
      {
        type: 'heading',
        text: 'When to Migrate: The Microservices Signal'
      },
      {
        type: 'paragraph',
        text: 'You should only consider migrating to microservices when different parts of your application have completely conflicting scaling requirements (e.g., a real-time chat module requiring massive concurrent web sockets vs. a background report generator running heavy overnight tasks).'
      },
      {
        type: 'code',
        code: `# Typical API Gateway route mapping for microservices setup
# Forward specific requests to targeted decoupled backend microservices
routes:
  - path: /api/v1/auth/*
    service: authentication-microservice:8081
  - path: /api/v1/billing/*
    service: transactional-payment-microservice:8082`
      },
      {
        type: 'quote',
        text: 'Migrating to microservices before your business is ready will increase complexity without providing any meaningful speed. Only split when organizational boundaries demand it.'
      },
      {
        type: 'paragraph',
        text: 'Making an informed architectural decision based on team size, system scaling patterns, and operational readiness will protect your application velocity and prevent unnecessary hosting budgets.'
      }
    ]
  },
  {
    slug: 'aws-data-lake-enterprise-guide',
    title: 'Building a Data Lake on AWS: Step-by-Step for Enterprises',
    excerpt: 'Consolidate database silos and build a scalable repository for raw data. Here is the step-by-step guide to AWS Glue, Athena, and Amazon S3.',
    category: 'Custom Software',
    author: 'Dr. Ramesh Nair',
    role: 'Director of AI & ML',
    date: 'March 18, 2026',
    readTime: '7 min read',
    tags: ['Data Lake', 'AWS Glue', 'Amazon S3', 'Athena', 'Analytics'],
    accentColor: '#2563eb',
    coverGradient: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
    content: [
      {
        type: 'paragraph',
        text: 'Modern enterprises gather immense amounts of information from legacy transactional databases, mobile application analytics, third-party sales APIs, and IoT devices. However, this raw data is usually isolated across different departmental databases. Building a secure cloud-based Data Lake allows you to consolidate these siloed datasets, run large-scale business intelligence queries, and train advanced predictive models.'
      },
      {
        type: 'heading',
        text: 'Phase 1: Setting up Amazon S3 as the Storage Layer'
      },
      {
        type: 'paragraph',
        text: 'Amazon S3 is the perfect foundation for a data lake because of its high durability, scalable pricing, and native integration with analytic engines. When building your storage layer, divide S3 buckets into raw (landing), cleansed (processed), and curated (analytics-ready) zones.'
      },
      {
        type: 'list',
        items: [
          'Landing Zone: Store raw CSV, JSON, or XML feeds as they arrive from client endpoints.',
          'Cleansed Zone: Standardize schemas, clean empty entries, and convert data format into highly optimized Parquet tables.',
          'Curated Zone: Create aggregated datasets tailored to business analytics tools like Amazon QuickSight.'
        ]
      },
      {
        type: 'heading',
        text: 'Phase 2: Metadata Cataloging with AWS Glue & Athena'
      },
      {
        type: 'paragraph',
        text: 'To query your data lake using standard SQL without spinning up massive database servers, you must crawl your S3 paths using AWS Glue crawlers and index the tables in the Glue Data Catalog.'
      },
      {
        type: 'code',
        code: `-- SQL query syntax used directly inside Amazon Athena
-- Easily search through cold files stored in S3 using standard SQL
SELECT category, COUNT(1) as total_interactions, AVG(duration_seconds) as average_time
FROM "cleansed_data_lake"."user_interactions"
WHERE year = '2026' AND month = '05'
GROUP BY category
ORDER BY total_interactions DESC;`
      },
      {
        type: 'quote',
        text: 'A data lake without proper cataloging and governance becomes a data swamp. Designing structured S3 layouts and Glue schemas is critical for successful analytics.'
      },
      {
        type: 'paragraph',
        text: 'By implementing a structured, secure Amazon S3 and AWS Glue workflow, enterprises can unlock deep analytical insights, decrease server hosting fees, and build robust foundations for data science pipelines.'
      }
    ]
  }
];

// ── Centralized Categories list ──
export const categories = [
  'All',
  'Cloud & DevOps',
  'Cybersecurity',
  'AI & Automation',
  'Custom Software',
  'Case Studies'
];

// Helper to clean HTML, removing script/style tags and their inner content entirely
const cleanHtmlText = (html) => {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
};

export default function Blog() {
  const [posts, setPosts] = useState(blogPosts);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Fetch Blogger feed on mount via JSONP to fully bypass CORS restrictions on localhost and in production
  useEffect(() => {
    const fetchBloggerFeedJSONP = () => {
      // Define a unique dynamic callback name to prevent namespace clashes
      const callbackName = 'blogger_callback_' + Math.floor(Math.random() * 1000000);
      
      // Assign the callback to the global window object
      window[callbackName] = (data) => {
        cleanup();
        
        try {
          if (data && data.feed && data.feed.entry && data.feed.entry.length > 0) {
            const accentColors = ['#06b6d4', '#ef4444', '#7c3aed', '#10b981', '#2563eb'];
            const parsed = data.feed.entry.map((entry, idx) => {
              const alternateLink = entry.link.find(l => l.rel === 'alternate');
              const redirectUrl = alternateLink ? alternateLink.href : 'https://blog.wayug.com';
              const slug = alternateLink ? redirectUrl.split('/').pop().replace('.html', '') : `post-${idx}`;
              const title = entry.title.$t;
              const rawContent = entry.content ? entry.content.$t : '';
              
              // Extract first image from HTML content to use as cover thumbnail
              const imgRegex = /<img[^>]+src="([^">]+)"/;
              const imgMatch = rawContent.match(imgRegex);
              const coverImage = imgMatch ? imgMatch[1] : (entry.media$thumbnail ? entry.media$thumbnail.url : null);
              
              // Excerpt extraction
              let excerpt = entry.summary ? cleanHtmlText(entry.summary.$t) : '';
              if (!excerpt && rawContent) {
                excerpt = cleanHtmlText(rawContent);
              }
              if (excerpt.length > 160) {
                excerpt = excerpt.substring(0, 160).trim() + '...';
              }
              
              // Date formatting
              const pubDate = new Date(entry.published.$t);
              const date = pubDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
              
              // Tags and category sorting
              const tags = entry.category ? entry.category.map(c => c.term) : ['Insights'];
              
              // Auto map Blogger labels/tags to closest primary Web solutions category
              let category = 'Cloud & DevOps';
              const lowerTags = tags.map(t => t.toLowerCase());
              if (lowerTags.some(t => t.includes('security') || t.includes('cyber') || t.includes('iso'))) {
                category = 'Cybersecurity';
              } else if (lowerTags.some(t => t.includes('ai') || t.includes('automation') || t.includes('ml') || t.includes('rpa'))) {
                category = 'AI & Automation';
              } else if (lowerTags.some(t => t.includes('software') || t.includes('dev') || t.includes('code') || t.includes('aws') || t.includes('kubernetes'))) {
                category = 'Custom Software';
              } else if (lowerTags.some(t => t.includes('case') || t.includes('study') || t.includes('costs'))) {
                category = 'Case Studies';
              } else if (tags[0]) {
                category = tags[0]; // Fallback to first label directly
              }
              
              const accentColor = accentColors[idx % accentColors.length];
              const gradients = [
                'linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)',
                'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
                'linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)',
                'linear-gradient(135deg, #064e3b 0%, #10b981 100%)',
                'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)'
              ];
              const coverGradient = gradients[idx % gradients.length];
              
              // Estimating read time
              const wordsCount = rawContent.split(/\s+/).length;
              const readTime = `${Math.max(3, Math.ceil(wordsCount / 200))} min read`;

              return {
                slug,
                title,
                excerpt,
                category,
                author: entry.author && entry.author[0] && entry.author[0].name ? entry.author[0].name.$t : 'Wayug Contributor',
                role: 'Consultant',
                date,
                readTime,
                tags,
                accentColor,
                coverGradient,
                coverImage,
                redirectUrl
              };
            });
            
            setPosts(parsed);
          }
        } catch (err) {
          console.warn('Error parsing feed:', err);
        } finally {
          setIsLoading(false);
        }
      };

      // Create and inject script tag
      const script = document.createElement('script');
      script.src = `https://blog.wayug.com/feeds/posts/default?alt=json-in-script&callback=${callbackName}`;
      script.async = true;
      script.id = 'blogger-feed-script';
      
      // Cleanup helper to keep global namespace and DOM pristine
      const cleanup = () => {
        const existingScript = document.getElementById('blogger-feed-script');
        if (existingScript) existingScript.remove();
        delete window[callbackName];
      };

      // Request Timeout fallback (5 seconds)
      const timer = setTimeout(() => {
        cleanup();
        console.warn('Blogger JSONP request timed out. Serving local posts fallback.');
        setIsLoading(false);
      }, 5000);

      script.onload = () => clearTimeout(timer);
      script.onerror = () => {
        clearTimeout(timer);
        cleanup();
        console.warn('Blogger JSONP script failed to load. Serving local posts fallback.');
        setIsLoading(false);
      };

      document.head.appendChild(script);

      return () => {
        clearTimeout(timer);
        cleanup();
      };
    };
    
    fetchBloggerFeedJSONP();
  }, []);

  // Filter posts based on Category and Search Query
  const filteredPosts = posts.filter((post) => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase())) ||
      post.category.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  // Flagship/Featured post is the very first post in the array
  const featuredPost = filteredPosts[0] || posts[0];

  // For the grid, we show all posts that match, excluding the featured post only when in 'All' category and search is empty
  const isDefaultView = selectedCategory === 'All' && searchQuery === '';
  const gridPosts = isDefaultView && filteredPosts.length > 1
    ? filteredPosts.slice(1)
    : filteredPosts;

  // Renders simple aesthetic inline loader while feed queries
  if (isLoading) {
    return (
      <div className="blog-section" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <div className="loading-spinner" style={{ border: '3px solid rgba(255,255,255,0.05)', borderTop: '3px solid #06b6d4', borderRadius: '50%', width: '36px', height: '36px', animation: 'spin 0.8s linear infinite', margin: '0 auto 1.5rem' }}></div>
          <p style={{ color: '#737373', fontSize: '0.92rem', letterSpacing: '0.02em' }}>Fetching active posts from blog.wayug.com...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-section">
      <div className="container">
        
        {/* Header Eyebrow and Titles */}
        <div className="blog-header text-center">
          <span className="section-label">Enterprise Insights</span>
          <h1 className="section-title">Guides, Case Studies &amp; <span className="gradient-text">Engineering Blog</span></h1>
          <p className="section-subtitle">
            Stay ahead with technical checklists, compliance deep-dives, and AI engineering methodologies written by Wayug's senior consultants.
          </p>
          <div style={{ marginTop: '1.5rem' }}>
            <a href="https://blog.wayug.com/" target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              View our live Blogger Feed <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* Filter & Search Bar */}
        {/* <div className="blog-controls-row">
          <div className="category-filters" role="tablist" aria-label="Blog categories">
            {categories.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={selectedCategory === cat}
                className={`category-pill${selectedCategory === cat ? ' active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="search-box-wrap">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search articles or tags..."
              className="blog-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search articles"
            />
          </div>
        </div> */}

        {/* ── Featured Post Hero Card ── */}
        {isDefaultView && featuredPost && (
          <motion.div 
            className="blog-featured-card"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="featured-cover" style={{ background: featuredPost.coverImage ? `url(${featuredPost.coverImage}) center/cover no-repeat` : featuredPost.coverGradient }}>
              <div className="featured-cover-overlay" />
              <div className="featured-badge">Featured Article</div>
            </div>
            
            <div className="featured-content">
              <div className="post-meta-row">
                <span className="post-category" style={{ color: featuredPost.accentColor, backgroundColor: `${featuredPost.accentColor}15` }}>
                  {featuredPost.category}
                </span>
                <span className="meta-item"><Calendar size={14} /> {featuredPost.date}</span>
                <span className="meta-item"><Clock size={14} /> {featuredPost.readTime}</span>
              </div>

              <h2 className="featured-title">
                <Link to={`/blog/${featuredPost.slug}`}>{featuredPost.title}</Link>
              </h2>

              <p className="featured-excerpt">{featuredPost.excerpt}</p>

              <div className="post-author-row">
                <div className="author-avatar" style={{ backgroundColor: `${featuredPost.accentColor}25`, color: featuredPost.accentColor }}>
                  <User size={16} />
                </div>
                <div>
                  <span className="author-name">{featuredPost.author}</span>
                  <span className="author-role">{featuredPost.role}</span>
                </div>
              </div>

              <div className="featured-footer-btn">
                <Link to={`/blog/${featuredPost.slug}`} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  Read Full Article <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Articles Grid ── */}
        {gridPosts.length > 0 ? (
          <div className="blog-grid">
            {gridPosts.map((post, idx) => (
              <motion.article 
                key={post.slug} 
                className="blog-card"
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
              >
                <div className="card-cover" style={{ background: post.coverImage ? `url(${post.coverImage}) center/cover no-repeat` : post.coverGradient }}>
                  <div className="card-cover-overlay" />
                  <span className="post-category-tag" style={{ color: post.accentColor, backgroundColor: `${post.accentColor}18` }}>
                    {post.category}
                  </span>
                </div>

                <div className="card-body">
                  <div className="card-meta">
                    <span className="meta-item"><Calendar size={13} /> {post.date}</span>
                    <span className="meta-item"><Clock size={13} /> {post.readTime}</span>
                  </div>

                  <h3 className="card-title">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </h3>

                  <p className="card-excerpt">{post.excerpt}</p>

                  <div className="card-tags-row">
                    {post.tags.map((tag) => (
                      <span key={tag} className="tag-badge">#{tag}</span>
                    ))}
                  </div>

                  <div className="card-footer-row">
                    <div className="author-summary">
                      <span className="author-name-small">{post.author}</span>
                    </div>
                    <Link to={`/blog/${post.slug}`} className="read-more-link" style={{ color: post.accentColor }}>
                      Read <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="blog-empty text-center">
            <BookOpen size={48} className="empty-icon" />
            <h3>No articles found</h3>
            <p>We couldn't find any articles matching "{searchQuery}" under "{selectedCategory}".</p>
            <button className="btn btn-outline" onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}>
              Reset Filters
            </button>
          </div>
        )}

        {/* ── WhatsApp Newsletter CTA ── */}
        <div className="blog-newsletter-section">
          <div className="newsletter-card-inner">
            <span className="cta-eyebrow">Stay Compliant &amp; Secure</span>
            <h2>Get monthly technical guides sent straight to your device</h2>
            <p>Join 200+ CTOs and technical managers who receive our direct breakdown of compliance updates, security threats, and cost-reduction methodologies.</p>
            <a 
              href="https://wa.me/918287999236?text=Hi%20Wayug!%20I%20would%20like%20to%20subscribe%20to%20your%20technical%20blog%20and%20monthly%20newsletter." 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '1.5rem' }}
            >
              Subscribe via WhatsApp <ArrowRight size={16} />
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
